import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CCard, CCardBody, CCardFooter, CCol, CRow } from "@coreui/react-pro";
import {
  type TradingStrategyDto,
  isFreeStrategy,
} from "../../../api/hooks/useTradingStrategies";
import { StrategyIdentityBand } from "../../trading-strategies/components/StrategyIdentityBand";
import { StrategyCurve } from "../../trading-strategies/components/StrategyCurve";
import { StrategySidesTable } from "../../trading-strategies/components/StrategySidesTable";
import { BacktestNote } from "../../trading-strategies/components/BacktestNote";
import { formatPriceUsd } from "../../../lib/money";

interface CrossSellStripProps {
  /** One entry per strategy, each holding its sides in side order. */
  strategies: TradingStrategyDto[][];
}

export function CrossSellStrip({ strategies }: CrossSellStripProps) {
  return (
    <CRow xs={{ gutter: 3 }}>
      {strategies.map((sides) => (
        // The same steps the browse grid uses, deliberately: it is the same card, and it needs the
        // same width to hold its side table and two lines of description. Three across from md
        // gave a ~250px card on an iPad Air, where the description truncated and the stat labels
        // each wrapped to two lines. Desktop is unaffected — xl is still three across.
        <CCol md={6} xl={4} key={sides[0].id}>
          <CrossSellCard sides={sides} />
        </CCol>
      ))}
      <CCol xs={12}>
        {/* One note for the band, not one per card: the strip is a self-contained section and
            these are the same cards the browse grid uses. */}
        <BacktestNote className="mt-0" />
      </CCol>
    </CRow>
  );
}

/** Deliberately a copy of the browse card in TradingStrategies.tsx — change both together. */
function CrossSellCard({ sides }: { sides: TradingStrategyDto[] }) {
  // The browse namespace, not the dashboard one, so the two cards cannot be translated apart.
  const { t } = useTranslation("tradingStrategies");
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);

  const face = sides[0];
  const detailPath = `/strategies/${face.id}`;

  const activate = () => navigate(detailPath);
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      activate();
    }
  };

  return (
    <CCard
      className="h-100"
      style={{
        cursor: "pointer",
        transition: "transform 120ms ease, box-shadow 120ms ease",
        transform: hovered ? "translateY(-2px)" : "none",
        boxShadow: hovered ? "0 0.5rem 1rem rgba(0,0,0,0.08)" : undefined,
        overflow: "hidden",
      }}
      role="button"
      tabIndex={0}
      onClick={activate}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-label={`View details for ${face.publicName}`}
    >
      <StrategyIdentityBand strategy={face} />
      <CCardBody className="d-flex flex-column">
        <h5 className="mb-1">{face.publicName}</h5>
        <p
          className="text-body-secondary small mb-3"
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {face.description}
        </p>

        <div className="mt-auto">
          <StrategyCurve strategy={face} emphasised={hovered} />
        </div>
      </CCardBody>

      <CCardFooter className="bg-body-tertiary">
        <StrategySidesTable
          sides={sides}
          endColumns={[
            {
              width: "3rem",
              heading: t("card.sides.perMonth"),
              cell: (side) => (isFreeStrategy(side) ? t("price.free") : formatPriceUsd(side.monthlyPriceUsd)),
            },
          ]}
          sideHref={(side) => `/strategies/${side.id}`}
        />
      </CCardFooter>
    </CCard>
  );
}
